import React, { useEffect, useState } from 'react';
import {
  Heading,
  VStack,
  Text,
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel,
  AccordionItem,
  Accordion,
  AccordionButton,
  AccordionIcon,
  Box,
  AccordionPanel,
  Td,
  Table,
  Thead,
  Th,
  TableContainer,
  Tbody,
  Tr,
  Checkbox,
  HStack,
  Button,
  Badge,
  Container,
  Icon,
  Tooltip,
  useColorModeValue
} from "@chakra-ui/react";
import { FaRunning, FaMoneyBillWave, FaCalendarCheck, FaFutbol } from 'react-icons/fa';
import MemberActivityDetails from '../components/member-activity-details';
import { useAuth } from '../components/auth';
import { getMember } from '../services/member-service';
import { Activity } from '../models/activity';
import { getActivitiesFeesByMember } from "../services/product-service";
import { ActivityProductPayment } from "../models/activities-fees-by-member-response";
import { HTTP_STATUS_CODE_OK } from "../constants";
import { isMobile } from "react-device-detect";
import { PeriodicProduct } from "../models/periodic-product";
import { useNavigate } from "react-router-dom";
import CurrencyData from "../components/currency-data";
import {MemberProductStatus} from "../models/periodic-product-payment";
import {badgeColor, getBadgeLabel, getDate, isBlockedByPreviousUnpaidMonths, paymentCheckboxIsDisabled, PREVIOUS_MONTHS_PAYMENT_REQUIRED_MESSAGE} from "../helpers";

function MyActivities(): JSX.Element {
  const auth = useAuth();
  const navigate = useNavigate();
  const [memberActivities, setMemberActivities] = useState<Activity[]>([]);
  const [memberActivitiesNoInsurance, setMemberActivitiesNoInsurance] = useState<Activity[]>([]);
  const [activitiesFeesYears, setActivitiesFeesYears] = useState<string[]>([]);
  const [activitiesFeesPayments, setActivitiesFeesPayments] = useState(new Map<string, ActivityProductPayment[]>());
  const [insuranceProductsPaid, setInsuranceProductsPaid] = useState<PeriodicProduct[]>([]);
  const [activitiesFeesToPayPayments, setActivitiesFeesToPayPayments] = useState<MemberProductStatus[]>([]);
  const [subtitleText, setSubtitleText] = useState("");


  const cardBg = useColorModeValue('white', 'gray.800');
  const headerBg = useColorModeValue('green.100', 'green.800');
  const borderColor = useColorModeValue('gray.200', 'gray.600');
  const textColor = useColorModeValue('gray.800', 'gray.100');
  const secondaryTextColor = useColorModeValue('gray.600', 'gray.300');

  useEffect(() => {
    const getMemberActivities = async (): Promise<void> => {
      const member = (await getMember(auth?.user?.selectedMember?.id)).response;
      if (member != null) {
        member.memberActivities.length > 0
          ? setSubtitleText("Estas son las actividades en las que te estás dado de alta.")
          : setSubtitleText("Actualmente no estás dado de alta en ninguna actividad.");

        setMemberActivities(member?.memberActivities.map((x) => x.activity));
        setMemberActivitiesNoInsurance(member?.memberActivities.filter((activity) => !activity.activity.isInsurance && !activity.endDate).map(x => x.activity));
        const activitiesFeesResponse = await getActivitiesFeesByMember(member.id);
        if (activitiesFeesResponse.statusCode === HTTP_STATUS_CODE_OK && activitiesFeesResponse.response) {
          const keys = Object.keys(activitiesFeesResponse.response.activityFeesPayments);
          const entries = Object.entries(activitiesFeesResponse.response.activityFeesPayments);
          const map = new Map(entries);
          setActivitiesFeesYears(keys.reverse());
          setActivitiesFeesPayments(map);
          setInsuranceProductsPaid(
            getListOfPaidInsuranceProducts(
              map,
              member?.memberActivities.map((x) => x.activity)
            ) ?? []
          );
        }
      }
    }

    getMemberActivities().catch(() => {});
  }, [auth?.user?.member?.id]);

  const getListOfPaidInsuranceProducts = (map: Map<string, ActivityProductPayment[]>, memberActivities: Activity[]) => {
    const insuranceActivity = memberActivities.find((insurance) => insurance.isInsurance);
    if (!insuranceActivity) return;

    const list: PeriodicProduct[] = [];
    map.forEach((activityProductPaymentArray) => {
      activityProductPaymentArray.map((activityProductPayment) => {
        if (activityProductPayment.activityId === insuranceActivity.id) {
          activityProductPayment.productPayments.map((productPayment): void => {
            if (productPayment.payments && productPayment.payments.length > 0) {
              list.push(productPayment.product);
            }
          })
        }
      })
    });
    return list;
  }

  const getInsuranceProductToPay = (insuranceActivityId: string, startDate: Date, endDate: Date) => {
    const year = new Date(startDate).getUTCFullYear().toString();
    const insuranceActivityPayments = activitiesFeesPayments.get(year)?.find((activityProductPayment) => activityProductPayment.activityId === insuranceActivityId);
    return insuranceActivityPayments?.productPayments.find((productPayment) => productPayment.product.startDate >= startDate && productPayment.product.endDate <= endDate)?.product;
  }

  const getInsuranceProductToPayPayment = (insuranceActivityId: string, startDate: Date, endDate: Date) => {
    const year = new Date(startDate).getUTCFullYear().toString();
    const insuranceActivityPayments = activitiesFeesPayments.get(year)?.find((activityProductPayment) => activityProductPayment.activityId === insuranceActivityId);
    return insuranceActivityPayments?.productPayments.find((productPayment) => productPayment.product.startDate >= startDate && productPayment.product.endDate <= endDate);
  }

  const handleCheckboxChange = (activityId: string, periodicProductCheckbox: MemberProductStatus) => {
    const index = activitiesFeesToPayPayments.findIndex((periodicProductPayment) => periodicProductPayment.product.id === periodicProductCheckbox.product.id);
    const activity = memberActivities.find((activity) => activity.id === activityId);
    if (!activity) return;

    let paidInsuranceIndex = -1; // List of already paid insurance products
    let toPayInsuranceIndex = -1; // List of insurance products going to be paid
    let insuranceActivity: Activity | undefined;
    if (activity.isInsuranceRequired) {
      insuranceActivity = memberActivities.find((insurance) => insurance.isInsurance);
      if (!insuranceActivity) return;

      paidInsuranceIndex = insuranceProductsPaid.findIndex((insuranceProduct) => insuranceProduct.activityId === insuranceActivity?.id && insuranceProduct.startDate >= periodicProductCheckbox.product.startDate && insuranceProduct.endDate <= periodicProductCheckbox.product.endDate);
      toPayInsuranceIndex = activitiesFeesToPayPayments.findIndex((insuranceProductPayment) => insuranceProductPayment.product.activityId === insuranceActivity?.id && insuranceProductPayment.product.startDate >= periodicProductCheckbox.product.startDate && insuranceProductPayment.product.endDate <= periodicProductCheckbox.product.endDate);
    }

    if (index >= 0) {
      setActivitiesFeesToPayPayments(activitiesFeesToPayPayments.filter((ppp) => ppp.product.id !== periodicProductCheckbox.product.id));
      if (activity.isInsuranceRequired && toPayInsuranceIndex >= 0 && insuranceActivity) {
        const insuranceProduct = getInsuranceProductToPay(insuranceActivity.id, periodicProductCheckbox.product.startDate, periodicProductCheckbox.product.endDate);
        if (insuranceProduct) {
          setActivitiesFeesToPayPayments(activitiesFeesToPayPayments.filter((ppp) => ppp.product.id !== periodicProductCheckbox.product.id && ppp.product.id !== insuranceProduct.id));
        }
      }
    }
    else {
      setActivitiesFeesToPayPayments([...activitiesFeesToPayPayments, periodicProductCheckbox]);
      if (activity.isInsuranceRequired) {
        if (paidInsuranceIndex < 0 && toPayInsuranceIndex < 0 && insuranceActivity) {
          const insuranceProduct = getInsuranceProductToPay(insuranceActivity.id, periodicProductCheckbox.product.startDate, periodicProductCheckbox.product.endDate);
          const insuranceProductPayment = getInsuranceProductToPayPayment(insuranceActivity.id, periodicProductCheckbox.product.startDate, periodicProductCheckbox.product.endDate);
          if (insuranceProduct && insuranceProductPayment) {
            setActivitiesFeesToPayPayments([...activitiesFeesToPayPayments, periodicProductCheckbox, insuranceProductPayment]);
          }
        }
      }
    }
  }

  const getAllActivityProducts = (activityId: string): MemberProductStatus[] => {
    const result: MemberProductStatus[] = [];
    activitiesFeesPayments.forEach((activityFees) => {
      const activityFee = activityFees.find((fee) => fee.activityId === activityId);
      if (activityFee) {
        result.push(...activityFee.productPayments);
      }
    });
    return result;
  };

  const getPayCheckbox = (activityId: string, activityFee: MemberProductStatus) => {
    const allActivityProducts = getAllActivityProducts(activityId);
    const isChecked = activitiesFeesToPayPayments.some((payment) => payment.product.id === activityFee.product.id);
    const isBlockedByPreviousMonths = isBlockedByPreviousUnpaidMonths(
      activityFee,
      allActivityProducts,
      activitiesFeesToPayPayments
    );
    const checkbox = (
      <Checkbox
        borderColor="black"
        isChecked={isChecked}
        disabled={paymentCheckboxIsDisabled(activityFee, allActivityProducts, activitiesFeesToPayPayments, isChecked)}
        onChange={() => { handleCheckboxChange(activityId, activityFee); }}
      />
    );

    if (isBlockedByPreviousMonths && !isChecked) {
      return (
        <Tooltip label={PREVIOUS_MONTHS_PAYMENT_REQUIRED_MESSAGE} placement="top">
          <Box display="inline-block">{checkbox}</Box>
        </Tooltip>
      );
    }

    return checkbox;
  };

  const getAccordionItemTableRows = (year: string, activityId: string) => {
    const activityFees = activitiesFeesPayments.get(year)?.find((activityFee) => activityFee.activityId === activityId);
    return activityFees?.productPayments.map((activityFee) => {

        return (
          <Tr key={`${activityFee.product.id}-${year}`}>
            <Td>{ isMobile ? activityFee.product.shortName : activityFee.product.name }</Td>
            <Td>
              <Badge colorScheme={badgeColor(activityFee)} variant="solid">
                {getBadgeLabel(activityFee, 'Abonado')}
              </Badge>
            </Td>
            <Td>{activityFee.payments?.map(pd => <>{getDate(pd.paymentDate, 'dd-mm-yyyy HH:MM:ss', false)}<br/></>)}</Td>
            <Td><CurrencyData value={activityFee.remainingPrice === 0 ? activityFee.paidAmount : activityFee.remainingPrice}/></Td>
            <Td>{activityFee.paidAmount === 0 ? <></> : <CurrencyData value={activityFee.paidAmount}/>}</Td>
            <Td>{getPayCheckbox(activityId, activityFee)}</Td>
          </Tr>
        )
    })

  }

  const getAccordionItemTable = (year: string, activityId: string) => {
    return (
      <TableContainer>
        <Table variant='simple'>
          <Thead>
            <Tr>
              <Th>Detalle</Th>
              <Th>Estado</Th>
              <Th>Fecha de pago</Th>
              <Th>Valor</Th>
              <Th>Valor abonado</Th>
              <Th>Abonar</Th>
            </Tr>
          </Thead>
          <Tbody>
            {getAccordionItemTableRows(year, activityId)}
          </Tbody>
        </Table>
      </TableContainer>
    )
  }

  const getAccordionItems = (activityId: string) => {
    const yearsWithRows = activitiesFeesYears.filter((year) => {
      const activityFees = activitiesFeesPayments.get(year)?.find((activityFee) => activityFee.activityId === activityId);
      return (activityFees?.productPayments?.length ?? 0) > 0;
    });

    return yearsWithRows.map((year) => {
      return (
        <AccordionItem key={`${activityId}-${year}`}>
          <h2>
            <AccordionButton>
              <Box as="span" flex='1' textAlign='left'>
                <HStack>
                  <Icon as={FaCalendarCheck} color="green.500" />
                  <Text>{year}</Text>
                </HStack>
              </Box>
              <AccordionIcon />
            </AccordionButton>
          </h2>
          <AccordionPanel pb={4}>
            {getAccordionItemTable(year, activityId)}
          </AccordionPanel>
        </AccordionItem>
      )
    });
  }

  const getActivitiesYearsAccordion = (activityId: string) => {
    return (
      <Accordion defaultIndex={[0]} allowMultiple width="100%">
        {getAccordionItems(activityId)}
      </Accordion>
    )
  }

  const getTabsTitles = () => {
    return memberActivitiesNoInsurance.map((activity) => (
      <Tab key={activity.id}>
        {activity.name}
      </Tab>
    ));
  }

  const getTabsPanels = () => {
    return memberActivitiesNoInsurance.map((activity) => (
      <TabPanel key={activity.id} px={0} py={6}>
        <VStack spacing={6} align="stretch">
          <MemberActivityDetails activity={activity} />
          <Box
            bg={cardBg}
            p={6}
            borderRadius="lg"
            border="1px solid"
            borderColor={borderColor}
            boxShadow="lg"
          >
            <VStack spacing={4} align="stretch">
              <HStack>
                <Icon as={FaMoneyBillWave} color="green.500" />
                <Heading as="h3" size="md" color={textColor}>
                  Historial de pagos
                </Heading>
              </HStack>
              {getActivitiesYearsAccordion(activity.id)}
            </VStack>
          </Box>
        </VStack>
      </TabPanel>
    ));
  }

  const getActivitiesTabs = () => {
    return (
      <Tabs variant='line' colorScheme='blue' width="100%">
        <TabList>
          {getTabsTitles()}
        </TabList>
        <TabPanels>
          {getTabsPanels()}
        </TabPanels>
      </Tabs>
    )
  }

  const getPaymentTableRows = () => {
    return activitiesFeesToPayPayments.map((activityFeePayment) => {
      return (
        <Tr key={activityFeePayment.product.id}>
          <Td>{activityFeePayment.product.name}</Td>
          <Td><CurrencyData value={activityFeePayment.remainingPrice}/></Td>
        </Tr>
      )
    })
  }

  const handlePayButton = (): void => {
    navigate('/payment-page', {
      state: { productsToPay: activitiesFeesToPayPayments, paymentMethod: 'MercadoPago' }
    });
  };

  const getPaymentSection = () => {
    if (activitiesFeesToPayPayments.length === 0) return <></>

    return (
      <Box
        bg={cardBg}
        p={6}
        borderRadius="lg"
        border="1px solid"
        borderColor={borderColor}
        boxShadow="lg"
      >
        <VStack spacing={4} align="stretch">
          <HStack>
            <Icon as={FaMoneyBillWave} color="green.500" />
            <Heading as="h3" size="md" color={textColor}>
              Resumen de pago
            </Heading>
          </HStack>
          <TableContainer>
            <Table variant='simple'>
              <Thead>
                <Tr>
                  <Th>Detalle</Th>
                  <Th>Valor</Th>
                </Tr>
              </Thead>
              <Tbody>
                {getPaymentTableRows()}
              </Tbody>
            </Table>
          </TableContainer>
          <Button
            colorScheme="blue"
            onClick={handlePayButton}
            size="lg"
            width="full"
          >
            Pagar con MercadoPago
          </Button>
        </VStack>
      </Box>
    )
  }

  return (
    <Container maxW="container.xl" py={6}>
      <VStack spacing={6} align="stretch">
        <Box
          bg={headerBg}
          p={6}
          borderRadius="lg"
          border="1px solid"
          borderColor={borderColor}
          boxShadow="sm"
        >
        <HStack>
             <Icon as={FaFutbol} color="green.500" boxSize={6} />
             <Heading color={textColor}>Mis actividades</Heading>
           </HStack>
          <Text mt={2} color={secondaryTextColor}>
            {subtitleText}
          </Text>
        </Box>

        {/* Content */}
        <VStack spacing={6} align="stretch">
          {/* Activities Tabs */}
          {memberActivitiesNoInsurance.length > 0 && (
            <Box
              bg={cardBg}
              borderRadius="lg"
              border="1px solid"
              borderColor={borderColor}
              boxShadow="sm"
              overflow="hidden"
            >
              {getActivitiesTabs()}
            </Box>
          )}

          {getPaymentSection()}
        </VStack>
      </VStack>
    </Container>
  )
}

export default MyActivities;
